import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// Read-only client for the /rest/index-constituents surface exposed by
// f2-admin-service. Powers the "Index Constituents" panel under
// /admin/common-data. Data comes from Redis LSEG:INDEX_CONSTITUENTS:*
// keys, populated by lseg-service's pump_index_constituents.

export type IndexKey = 'SPX' | 'DJI' | 'NDX' | 'IXIC' | 'QQQ' | 'IWV' | 'IWM';

export interface IndexConstituentRow {
  TickerSymbol: string;
  // Future: MainIndexCommonName, etc. — kept as index signature for
  // whatever the pump surfaces.
  [k: string]: any;
}

export interface IndexSummary {
  index: string;
  count: number;
  sample: string[];
}

@Injectable({ providedIn: 'root' })
export class IndexConstituentsService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }
  private _base() { return `${env.apiUrl}/rest/index-constituents`; }

  async listAll(): Promise<IndexSummary[]> {
    const id = this.loadingService.add('Loading index list');
    // No trailing slash — Vercel's /rest/:path* rewrite doesn't match a
    // bare trailing slash and falls through to the SPA index.html,
    // which Angular's HttpClient then fails to parse as JSON. ER-GGG5JMV
    // + ER-PXF7VG3 + ER-KXHHFH9 + ER-D7BMX6G all landed here.
    const url = this._base();
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.indexes) ? res.indexes as IndexSummary[] : [];
    } catch (e: any) {
      this._toastErr('Index Constituents', e, 'F2A_INDEX_CONSTITUENTS_LIST_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async getOne(index: IndexKey): Promise<IndexConstituentRow[]> {
    const id = this.loadingService.add(`Loading ${index} constituents`);
    const url = `${this._base()}/${index}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.rows) ? res.rows as IndexConstituentRow[] : [];
    } catch (e: any) {
      // 404 is an EXPECTED outcome — the backend throws NotFound when
      // the LSEG:INDEX_CONSTITUENTS:<idx> key is unpopulated (DJI / NDX /
      // IXIC today have no pump feeding them). Return empty and let the
      // grid render its own zero-row state; don't stamp an ER for a
      // known-legit empty index.
      if (e?.status === 404) return [];
      this._toastErr(`Index ${index}`, e, 'F2A_INDEX_CONSTITUENTS_GETONE_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/common-data',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || 'application/json',
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
